import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ProductTypeOptions } from '@/types/product';

import { ImageCarousel } from '@/components/ImageCarousel';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Loader } from '@/components/Loader';
import { ProductLabel } from '@/components/ProductLabel';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, WithChildren } from '@/lib/types';

import type { Gallery } from '../ImageCarousel/context';
import { ProductCard } from '../ProductCard';
import {
  buildImages,
  getMediaDimensions,
  type Image,
  type Media,
  type MediaLarge,
  sortImagesByShotType,
} from './utils';

import * as S from './ImagesGallery.styles';

interface CommonProps {
  /**
   * BEC-24926: opt in to the compact mobile product detail layout - smaller
   * image, no share icon, wishlist level with the breadcrumb. Off by default so
   * other consumers (the retailer widget) keep their current sizing.
   */
  compactOnMobile?: boolean;
  media: Maybe<Media[]>;
  name?: string | undefined | null;
  price?: number | undefined;
  productType?: ProductTypeOptions;
  shareableProductData?: Record<PropertyKey, string | null | undefined>;
  sku?: string | undefined | null;
  tag?: Maybe<ReactNode>;
  theCurrency?: string | undefined;
  withIcons?: boolean;
}
interface PropsWithGallery extends CommonProps, WithChildren<false> {
  alt?: string;
  gallery: Gallery;
  mediaLarge: Maybe<MediaLarge[]>;
}

interface PropsWithoutGallery extends CommonProps, WithChildren<false> {
  alt?: string;
  gallery?: never;
  mediaLarge?: never;
}

export type ImagesGalleryProps = PropsWithGallery | PropsWithoutGallery;

export const ImagesGallery = ({
  alt,
  children,
  compactOnMobile,
  gallery,
  media,
  mediaLarge,
  name,
  price,
  productType = 'strap',
  shareableProductData,
  sku,
  tag,
  theCurrency,
  withIcons = true,
}: ImagesGalleryProps) => {
  const [images, setImages] = useState<Image[]>(
    buildImages(media, mediaLarge, productType),
  );

  const [loader, setLoader] = useState(false);
  const isInitial = useRef(true);

  const isPhotoSwipeReady = images.every(({ large }) => !!large?.height);
  const hasMedia = !!media?.length;

  const mediaSignature = useMemo(
    () => (media ?? []).map((m) => m.url).join('|'),
    [media],
  );
  const mediaLargeSignature = useMemo(
    () => (mediaLarge ?? []).map((m) => m.url).join('|'),
    [mediaLarge],
  );

  useEffect(() => {
    let cancelled = false;

    if (isInitial.current) {
      isInitial.current = false;
    } else {
      // only show loader when media update
      setLoader(true);
    }
    if (media?.length && mediaLarge?.length) {
      // Reset images synchronously so isPhotoSwipeReady becomes false while
      // dimensions load. This forces usePhotoSwipe to re-register its click
      // handler with the correct new-product DOM once heights are available,
      // preventing the stale dataSource bug (BEC-21881).
      setImages(buildImages(media, mediaLarge, productType));
      getMediaDimensions(media, mediaLarge).then((imagesWithDimensions) => {
        if (cancelled) return;
        setImages(sortImagesByShotType(imagesWithDimensions, productType));
        setLoader(false);
      });
    } else {
      setImages(buildImages(media, mediaLarge, productType));
      setLoader(false);
    }
    return () => {
      cancelled = true;
    };
  }, [mediaSignature, mediaLargeSignature, productType]);

  return (
    <S.GalleryWrapperCol
      $compactOnMobile={compactOnMobile}
      data-test-id='ImagesGalleryWrapper'
      md={6}
      {...generateDataTestId('imagesGallery', 'wrapper')}
    >
      {children}

      {withIcons && sku && (
        <S.GalleryIcons $compactOnMobile={compactOnMobile}>
          <S.ActionsWrapper
            $compactOnMobile={compactOnMobile}
            flexDirection='column'
          >
            <ProductCard.Favorite
              data-test-id='ImagesGalleryFavorite'
              name={name}
              price={price}
              sku={sku}
              theCurrency={theCurrency}
              type={productType}
            />
            {shareableProductData && (
              <ProductCard.Share
                positionRight
                shareableProductData={shareableProductData}
              />
            )}
          </S.ActionsWrapper>
        </S.GalleryIcons>
      )}

      <S.CarouselArea>
        {/* BEC-24926: the label is anchored to the image rather than to the
            icons row. On mobile that row moves up to share the breadcrumb's
            line, which would otherwise drag the label onto the crumbs. */}
        {withIcons && sku && tag && (
          <S.LabelWrapper>
            <ProductLabel text={tag} />
          </S.LabelWrapper>
        )}

        <ImageCarousel
          key={sku}
          gallery={gallery}
          isProcessing={!isPhotoSwipeReady}
        >
          <ImageCarousel.Carousel productType={productType}>
            {hasMedia ? (
              images.map(({ large, thumb }, i) => (
                <ImageCarousel.CarouselItem
                  key={thumb.url}
                  data-testid={`ImagesGalleryCarouselItem-${i}`}
                  index={i}
                  {...(large && {
                    height: large.height,
                    largeSrc: large.url,
                    width: large.width,
                  })}
                >
                  {thumb.url &&
                    (loader ? (
                      <S.CenterLoader data-testid='GLigor'>
                        <Loader />
                      </S.CenterLoader>
                    ) : (
                      <ProductCard.Image
                        alt={thumb.alt || `${alt} - image ${i + 1}`}
                        className='carousel-image'
                        data-testid={`ImagesGalleryImage-${i}`}
                        disableLoader={i === 0}
                        height={680}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        sizes='(max-width: 991px) 100vw,
                        (max-width: 1261px) 55vw,
                        680px'
                        src={thumb.url}
                        width={680}
                      />
                    ))}
                </ImageCarousel.CarouselItem>
              ))
            ) : (
              <ImagePlaceholder
                className='carousel-image'
                data-testid='ImagesGalleryImagePlaceholder'
                productType={productType}
              />
            )}
          </ImageCarousel.Carousel>
        </ImageCarousel>
      </S.CarouselArea>
    </S.GalleryWrapperCol>
  );
};
